import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchCollection } from '../lib/cms';
import CategoryBadge from '../components/CategoryBadge';
import HelpNotice from '../components/HelpNotice';

export default function OmSkatPage() {
  useSeo({
    title: 'Om skat',
    description: 'Skatunderlups vidensunivers: Generel og struktureret information om indkomstskat, moms, selskabsskat, ejendomsbeskatning og klagesager.',
  });

  const [topics, setTopics] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState('alle');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadTopics() {
      setLoading(true);
      try {
        const res = await fetchCollection('om_skat', {
          sort: 'order',
          order: 'asc',
          limit: 30,
        });
        if (isMounted) {
          setTopics(res.items || []);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Error loading om_skat topics', e);
        if (isMounted) setLoading(false);
      }
    }
    loadTopics();
    return () => { isMounted = false; };
  }, []);

  const filteredTopics = topics.filter((t) => {
    if (selectedAudience !== 'alle') {
      if (selectedAudience === 'privat' && t.audience !== 'privat' && t.audience !== 'begge') return false;
      if (selectedAudience === 'erhverv' && t.audience !== 'erhverv' && t.audience !== 'begge') return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchTitle = (t.title || '').toLowerCase().includes(q);
      const matchTopic = (t.topic || '').toLowerCase().includes(q);
      const matchSummary = (t.summary || '').toLowerCase().includes(q);
      if (!matchTitle && !matchTopic && !matchSummary) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-slate-200 bg-slate-50/70 py-10 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#fd7302]">
            <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
            <span>Redaktionelt vidensunivers</span>
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-[#010b27]">
            Om skat: Temaer & Vidensområder
          </h1>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600 leading-relaxed">
            Struktureret overblik over de centrale skatteretlige områder i Danmark. Dette vidensunivers udbygges løbende af redaktionen med baggrundsinformation, regelsæt og rettigheder.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Filter bar */}
        <div className="mb-8 rounded-md border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 shadow-xs">
          <div className="inline-flex rounded-sm border border-slate-200 bg-white p-0.5 max-w-xs">
            <button
              type="button"
              onClick={() => setSelectedAudience('alle')}
              className={`px-3 py-1.5 text-xs font-bold rounded-xs uppercase tracking-wider transition-colors ${
                selectedAudience === 'alle'
                  ? 'bg-[#010b27] text-white'
                  : 'text-slate-600 hover:text-[#010b27]'
              }`}
            >
              Alle emner
            </button>
            <button
              type="button"
              onClick={() => setSelectedAudience('privat')}
              className={`px-3 py-1.5 text-xs font-bold rounded-xs uppercase tracking-wider transition-colors ${
                selectedAudience === 'privat'
                  ? 'bg-[#010b27] text-white'
                  : 'text-slate-600 hover:text-[#010b27]'
              }`}
            >
              Private
            </button>
            <button
              type="button"
              onClick={() => setSelectedAudience('erhverv')}
              className={`px-3 py-1.5 text-xs font-bold rounded-xs uppercase tracking-wider transition-colors ${
                selectedAudience === 'erhverv'
                  ? 'bg-[#fd7302] text-white'
                  : 'text-slate-600 hover:text-[#fd7302]'
              }`}
            >
              Erhverv
            </button>
          </div>

          <div className="relative sm:w-72">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søg i vidensuniverset..."
              className="w-full rounded-sm border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-[#010b27] placeholder:text-slate-400 focus:border-[#fd7302] focus:outline-none focus:ring-1 focus:ring-[#fd7302]"
            />
            <i className="fa-solid fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400" aria-hidden="true" />
          </div>
        </div>

        {/* Topics Grid */}
        {loading ? (
          <div className="py-20 text-center font-serif text-slate-500">
            Henter vidensområder...
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-10 text-center text-sm text-slate-600">
            Ingen emner matcher søgningen.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTopics.map((item) => {
              const slug = item.slug?._ || item.slug || '';
              return (
                <article
                  key={item.id}
                  className="group rounded-md border border-slate-200 bg-white p-6 shadow-xs hover:border-[#fd7302]/60 hover:shadow-sm transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#fd7302]">
                        {item.topic}
                      </span>
                      <CategoryBadge audience={item.audience} size="xs" />
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-slate-100 text-[#010b27] group-hover:bg-[#fd7302] group-hover:text-white transition-colors">
                        <i className={`${item.icon_name || 'fa-solid fa-book'} text-base`} aria-hidden="true" />
                      </div>
                      <div>
                        <Link
                          to={`/om-skat/${slug}`}
                          className="block font-serif text-xl font-bold text-[#010b27] group-hover:text-[#fd7302] transition-colors leading-snug"
                        >
                          {item.title}
                        </Link>
                        <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-3">
                          {item.summary}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-[11px] text-slate-400 font-mono">
                      Redaktionelt emne
                    </span>
                    <Link
                      to={`/om-skat/${slug}`}
                      className="font-semibold text-[#fd7302] hover:text-[#e06500] inline-flex items-center gap-1"
                    >
                      <span>Læs om emnet</span>
                      <i className="fa-solid fa-arrow-right text-[9px]" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Editorial Notice on expanding universe */}
        <div className="mt-12 rounded-md border border-slate-200 bg-slate-50/70 p-6">
          <div className="flex items-start gap-4">
            <i className="fa-solid fa-circle-info text-[#fd7302] text-xl mt-0.5" aria-hidden="true" />
            <div>
              <h3 className="font-serif text-lg font-bold text-[#010b27]">
                Løbende udbygning af vidensuniverset
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Videnssektionen fungerer som et levende opslagsværk for danske skatteforhold. Indholdet udvides kontinuerligt i takt med nye lovforslag, domstolsafgørelser og principielle kendelser fra Skatteankestyrelsen.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-12">
          <HelpNotice variant="banner" />
        </div>
      </div>
    </div>
  );
}
